import { normName, SOURCE_LABELS, stageTitle, type Source, type Stage } from '@jt/core';
import {
  Archive,
  Bell,
  Bookmark,
  Briefcase,
  Calendar,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clipboard,
  Clock,
  Columns3,
  Download,
  Ellipsis,
  ExternalLink,
  Eye,
  FileText,
  Filter,
  Info,
  LayoutDashboard,
  Link,
  LogOut,
  Mail,
  Menu,
  MessageSquare,
  Paperclip,
  Pencil,
  Phone,
  Plug,
  Plus,
  RotateCcw,
  Search,
  Settings as Gear,
  Send,
  Shield,
  Sparkles,
  Table,
  Trash2,
  Trophy,
  Upload,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { CSSProperties, ReactNode, SVGProps } from 'react';

/** LinkedIn убрали из lucide (бренды), рисуем как в прототипе. */
function LinkedinIcon(p: SVGProps<SVGSVGElement> & { size?: number }) {
  const { size = 18, ...rest } = p;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M8 11v5M8 8v.01M12 16v-5M12 13a2 2 0 0 1 4 0v3" />
    </svg>
  );
}

const ICONS = {
  send: Send,
  clock: Clock,
  calendar: Calendar,
  trophy: Trophy,
  brief: Briefcase,
  overview: LayoutDashboard,
  file: FileText,
  msg: MessageSquare,
  users: Users,
  chart: ChartColumn,
  plug: Plug,
  plus: Plus,
  search: Search,
  gear: Gear,
  filter: Filter,
  board: Columns3,
  table: Table,
  more: Ellipsis,
  chevR: ChevronRight,
  chevD: ChevronDown,
  back: ChevronLeft,
  mail: Mail,
  clip: Clipboard,
  paperclip: Paperclip,
  sparkles: Sparkles,
  x: X,
  pencil: Pencil,
  ext: ExternalLink,
  bell: Bell,
  trash: Trash2,
  check: Check,
  phone: Phone,
  info: Info,
  upload: Upload,
  download: Download,
  link: Link,
  archive: Archive,
  menu: Menu,
  bookmark: Bookmark,
  eye: Eye,
  restore: RotateCcw,
  logout: LogOut,
  shield: Shield,
  linkedin: LinkedinIcon,
} as const;

export type IconName = keyof typeof ICONS;

export function Icon({ name, size = 18, style }: { name: IconName; size?: number; style?: CSSProperties }) {
  const C = ICONS[name] as LucideIcon | typeof LinkedinIcon;
  return <C className="i" size={size} strokeWidth={1.8} aria-hidden="true" style={style} />;
}

const PALETTE = [
  '#111827',
  '#2B5BE9',
  '#0EA5E9',
  '#7C3AED',
  '#DB2777',
  '#EA580C',
  '#16A34A',
  '#0891B2',
  '#B45309',
];
const hash = (s: string) => {
  let h = 0;
  for (const c of s) h = (h * 31 + c.charCodeAt(0)) | 0;
  return Math.abs(h);
};

/** Логотип-буква компании: цвет из хеша названия (как в прототипе). */
export function Logo({ name, size = 36 }: { name: string; size?: number }) {
  const letter = (name.match(/[\p{L}\p{N}]/u)?.[0] ?? '?').toUpperCase();
  return (
    <span
      className="logo"
      aria-hidden="true"
      style={
        {
          '--lc': PALETTE[hash(normName(name)) % PALETTE.length],
          width: size,
          height: size,
          fontSize: Math.round(size * 0.46),
        } as CSSProperties
      }
    >
      {letter}
    </span>
  );
}

/** Первые буквы двух первых слов; скобки, эмодзи и прочие знаки пропускаются («Анастасия (укр)» → «АУ»). */
export const initials = (n: string) =>
  n
    .split(/\s+/)
    .map((w) => w.match(/[\p{L}\p{N}]/u)?.[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?';

export function Avatar({ name, size = 28 }: { name: string; size?: number }) {
  return (
    <span
      className="ava"
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38) }}
    >
      {initials(name)}
    </span>
  );
}

const SRC_CLS: Partial<Record<Source, string>> = {
  LinkedIn: 'li',
  Glassdoor: 'gd',
  Telegram: 'tg',
  Email: 'em',
  InfoJobs: 'ij',
  Indeed: 'in',
  Talanto: 'ta',
};

export function SourceTag({ source }: { source: Source | string | null }) {
  if (!source) return null;
  const label = SOURCE_LABELS[source as Source] ?? source;
  return <span className={`tag ${SRC_CLS[source as Source] ?? ''}`}>{label}</span>;
}

export function StagePill({ stage }: { stage: Stage }) {
  return (
    <span className="spill" style={{ '--c': `var(--s-${stage})` } as CSSProperties}>
      {stageTitle(stage)}
    </span>
  );
}

export const STAGE_ICON: Record<Stage, IconName> = {
  saved: 'bookmark',
  applied: 'send',
  screening: 'users',
  screening_wait: 'clock',
  interview: 'calendar',
  interview_wait: 'clock',
  final: 'calendar',
  final_wait: 'clock',
  offer: 'trophy',
  rejected: 'x',
  ghosted: 'clock',
};

export function Empty({ title, text, children }: { title: string; text: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <h2>{title}</h2>
      <p>{text}</p>
      {children && <div className="btnrow">{children}</div>}
    </div>
  );
}

export function PageHead({ title, sub, children }: { title: string; sub?: string; children?: ReactNode }) {
  return (
    <header className="phead">
      <div>
        <h1>{title}</h1>
        {sub && <p>{sub}</p>}
      </div>
      {children && <div className="pacts">{children}</div>}
    </header>
  );
}

export const safeUrl = (u: string) => {
  const s = u.trim();
  if (!s) return '';
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

export function Spinner({ label = 'Загружаю…' }: { label?: string }) {
  return (
    <p className="muted" role="status">
      {label}
    </p>
  );
}
